import React, { useEffect, useMemo, useState } from 'react';
import './TeamPage.scss';
import SimpleBar from 'simplebar-react';
import Loader from '../../../shared/components/common/loader/Loader';
import ColorTag from '../../../shared/components/color-tag/ColorTag';
import RoleTag from '../../../shared/components/color-tag/RoleTag';
import {
  getTeam,
  removeUser,
  getGuestProjects,
  updateGuestProjects,
} from '../../../shared/services/team/TeamService';
import { TeamResponse } from '../../../shared/models/team/TeamResponse';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import GuestProjectsSelect from '../../../shared/components/guest-projects-select/GuestProjectsSelect';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getTokenData } from '../../../shared/services/auth/AuthService';
import { TokenData } from '../../../shared/models/auth/TokenData';
import { isAdminOrOwner } from '../../../shared/utils/utils';
import { Navigate, useNavigate } from 'react-router-dom';
import usePermission from '../../../shared/hooks/global/UsePermission';
import { PROJECTS } from '../../../shared/routes/path';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../shared/utils/mobile';

const TeamPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { accountResponse, refetchAccountSilently } = useAccount();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamResponse[] | null>(null);
  // Gość, któremu właśnie zmieniamy zakres projektów — `null` znaczy „okno zamknięte".
  const [editedGuest, setEditedGuest] = useState<TeamResponse | null>(null);
  const [guestProjectIds, setGuestProjectIds] = useState<number[]>([]);
  const [isScopeLoading, setIsScopeLoading] = useState(false);
  const [isSavingScope, setIsSavingScope] = useState(false);
  // Paginacja stoi POZA obszarem przewijania (jak na liście projektów), więc stronicowanie i
  // sortowanie robimy tu — `DataTable` dostaje gotowy wycinek i nie ma ich jak przejąć.
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);
  const [sortField, setSortField] = useState<string | null>('firstName');
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(1);
  const tokenData: TokenData | null = getTokenData();
  // Brak `read` = zakładki nie ma w menu ORAZ wejście z URL nic nie daje. Gość nie ma tu czego
  // szukać: to lista ludzi konta, na którym jest tylko widzem.
  const teamAccess = usePermission('TEAM');

  // `getTeam` zwraca obiekt błędu zamiast rzucać (`.catch((error) => error)`), więc przy 403 — a taki
  // dostaje gość — w stanie lądowało coś, co nie jest tablicą. Bez tej normalizacji sortowanie
  // wywracało całą stronę („team is not iterable") ZANIM zdążyło zadziałać przekierowanie niżej.
  const loadTeam = () => getTeam().then((data) => setTeam(Array.isArray(data) ? data : []));

  useEffect(() => {
    // Gość i tak zaraz wyleci na listę projektów — nie ma po co pytać o cudzy zespół i zbierać 403.
    if (!accountResponse || !teamAccess.read) {
      return;
    }

    loadTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountResponse, teamAccess.read]);

  const sortedTeam = useMemo(() => {
    if (!team || !sortField || !sortOrder) {
      return team ?? [];
    }

    // `isActive` jest boolem, reszta kolumn to tekst — bez tego rozróżnienia status sortowałby się
    // jako „false" < „true" alfabetycznie i zależał od języka.
    const sortValue = (member: TeamResponse) => {
      const raw = member[sortField as keyof TeamResponse];
      return typeof raw === 'boolean' ? Number(raw) : String(raw ?? '').toLowerCase();
    };

    return [...team].sort((a, b) => {
      const left = sortValue(a);
      const right = sortValue(b);
      if (left === right) {
        return 0;
      }

      return (left > right ? 1 : -1) * sortOrder;
    });
  }, [team, sortField, sortOrder]);

  const pageItems = sortedTeam.slice(first, first + rows);

  const confirmRemove = (member: TeamResponse) => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => remove(member),
    });
  };

  const remove = (member: TeamResponse) => {
    removeUser(member.id).then((isRemove) => {
      if (isRemove) {
        // Refresh team data after successful removal
        loadTeam();
        // The freed seat lives in the account context (`usersOnAccount` gates the invite button).
        void refetchAccountSilently();
        // Usunięcie ostatniego wiersza strony zostawiłoby pustą tabelę na nieistniejącej stronie.
        setFirst(0);
      }
    });
  };

  const openGuestScope = (member: TeamResponse) => {
    setEditedGuest(member);
    setGuestProjectIds([]);
    setIsScopeLoading(true);
    getGuestProjects(member.id)
      .then(setGuestProjectIds)
      .finally(() => setIsScopeLoading(false));
  };

  const saveGuestScope = () => {
    if (!editedGuest || guestProjectIds.length === 0) {
      return;
    }

    setIsSavingScope(true);
    updateGuestProjects(editedGuest.id, guestProjectIds).finally(() => {
      setIsSavingScope(false);
      setEditedGuest(null);
    });
  };

  const actionBodyTemplate = (member: TeamResponse) => {
    // Only show remove button if user is not OWNER and current user has permissions
    if (
      member.role === 'OWNER' ||
      !tokenData ||
      tokenData.user.id === member.id ||
      !accountResponse ||
      !isAdminOrOwner(accountResponse.role)
    ) {
      return null;
    }

    const removeButton = (
      <>
        <Button
          id={`btn-remove-user-${member.id}`}
          className="action-btn"
          onClick={() => confirmRemove(member)}
        >
          <span className="ico--trash ico--xs" />
        </Button>
        <CustomTooltip tooltipText="remove" targetRef={`btn-remove-user-${member.id}`} />
      </>
    );

    // Zakres projektów ma tylko gość, więc tylko przy nim są DWIE akcje i tylko tam menu ma sens.
    // Przy pozostałych rolach zostaje samo usunięcie — kebab kryjący jeden przycisk to klik więcej
    // bez żadnego zysku. Tooltip menu mówi to samo, co nagłówek kolumny, zamiast „Szczegółów".
    if (member.role !== 'GUEST') {
      return <div className="table__cell--actions">{removeButton}</div>;
    }

    return (
      <div className="table__cell--actions">
        <MoreBtn rowId={member.id} tooltipText="action">
          <div className="buttons-list">
            <Button
              id={`btn-edit-guest-scope-${member.id}`}
              className="action-btn"
              onClick={() => openGuestScope(member)}
            >
              <span className="ico--edit ico--xs" />
            </Button>
            <CustomTooltip
              tooltipText="guestEditProjects"
              targetRef={`btn-edit-guest-scope-${member.id}`}
            />
            {removeButton}
          </div>
        </MoreBtn>
      </div>
    );
  };

  // `<Navigate>` zamiast wołania `navigate()` w trakcie renderu: to ostatnie jest efektem ubocznym
  // w ciele komponentu i React Router wypisuje za nie ostrzeżenie o aktualizacji routera w renderze.
  if (accountResponse && !teamAccess.read) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (!team || !accountResponse) {
    return (
      <div className="team-page">
        <Loader />
      </div>
    );
  }

  return (
    <div className="team-page">
      <ConfirmDialog className="dialog-box" />

      <Dialog
        className="dialog-box"
        header={t('guestEditProjects')}
        visible={!!editedGuest}
        draggable={false}
        onHide={() => setEditedGuest(null)}
      >
        <div className="team-page__guest-scope">
          <div className="form__label">{t('guestProjectsAccess')}:</div>
          <GuestProjectsSelect
            value={guestProjectIds}
            onChange={setGuestProjectIds}
            isValueLoading={isScopeLoading}
          />
          {/* Pusty zakres to gość, który po zalogowaniu widzi pustkę — backend odrzuca go tak samo.
              Ostrzeżenie dopiero po wczytaniu zakresu, inaczej mignęłoby przy każdym otwarciu. */}
          {!isScopeLoading && guestProjectIds.length === 0 && <span>{t('guestNeedsProject')}</span>}
          <Button
            className="btn btn--sm"
            label={t('save')}
            disabled={isScopeLoading || guestProjectIds.length === 0 || isSavingScope}
            onClick={saveGuestScope}
          />
        </div>
      </Dialog>

      <div className="team-page__header">
        <div className="team-page__invite">
          <Button
            className="btn btn--sm"
            onClick={() => navigate('/settings/team/add')}
            disabled={accountResponse.usersOnAccount >= accountResponse.maxUsers}
          >
            <span className="ico--add ico--xs" />
            {t('inviteUser')}
          </Button>
        </div>
      </div>

      {/* Ten sam układ co lista projektów: SimpleBar właścicielem przewijania (spójny pasek w każdej
          przeglądarce), a `scrollable` jest warunkiem działania zamrożonej kolumny akcji. */}
      <div className="team-page__table table-content--simplebar">
        <div className="accordion-table-frame">
          {/* Telefon: karty przewijają się razem ze stroną, bez pudełka na 70% wysokości ekranu. */}
          <SimpleBar style={isMobile ? undefined : { maxHeight: '70vh' }} autoHide={false}>
            <DataTable
              stripedRows
              scrollable
              removableSort
              sortField={sortField ?? undefined}
              sortOrder={sortOrder}
              onSort={(e) => {
                setSortField(e.sortField || null);
                setSortOrder((e.sortOrder as 1 | -1 | 0) ?? 0);
                setFirst(0);
              }}
              value={pageItems}
              tableStyle={{ minWidth: '50rem' }}
              tableClassName="table"
              className="table-wrapper table-wrapper--accordion table-wrapper--stack team-table"
              responsiveLayout="stack"
            >
              <Column
                field="firstName"
                header={t('firstName')}
                sortable
                // Telefon: imię i nazwisko razem jako tytuł karty (kolumna nazwiska jest wtedy ukryta).
                body={(member: TeamResponse) =>
                  isMobile
                    ? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim()
                    : member.firstName
                }
                bodyClassName="table__cell--stack-title"
                headerClassName="table__header"
                style={{ minWidth: '120px' }}
              />
              <Column
                field="lastName"
                header={t('lastName')}
                sortable
                hidden={isMobile}
                headerClassName="table__header"
                style={{ minWidth: '120px' }}
              />
              {/* `invitedAt` = when the member joined the account (`user_accounts.created`); an ISO string, so
                  the text sort above orders it chronologically. Same date format as the rest of the panel. */}
              <Column
                field="invitedAt"
                header={t('invitedAt')}
                sortable
                body={(member: TeamResponse) => format(new Date(member.invitedAt), 'yyyy-MM-dd / HH:mm')}
                headerClassName="table__header"
                style={{ minWidth: '150px' }}
              />
              <Column
                field="email"
                header={t('emailAddress')}
                headerClassName="table__header"
                bodyClassName="table__cell--stack-wide"
                style={{ minWidth: '200px' }}
              />
              <Column
                field="role"
                header={t('role')}
                sortable
                body={(member: TeamResponse) => <RoleTag role={member.role} />}
                headerClassName="table__header"
                style={{ minWidth: '100px' }}
              />
              <Column
                field="isActive"
                header={t('status')}
                sortable
                body={(member) => (
                  <ColorTag color={member.isActive ? 'green' : 'orange'}>
                    {member.isActive ? t('active') : t('pending')}
                  </ColorTag>
                )}
                headerClassName="table__header"
                style={{ minWidth: '100px' }}
              />
              <Column
                header={t('action')}
                body={actionBodyTemplate}
                frozen
                alignFrozen="right"
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell--gap table__cell--stack-actions"
                align="center"
                style={{ width: '34px' }}
              />
            </DataTable>
          </SimpleBar>
          <Paginator
            className="pagination"
            first={first}
            rows={rows}
            totalRecords={sortedTeam.length}
            rowsPerPageOptions={[10, 25, 50]}
            currentPageReportTemplate={t('rowsNumber') + ':'}
            template={
              isMobile
                ? MOBILE_PAGINATOR_TEMPLATE
                : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
            }
            pageLinkSize={isMobile ? 3 : 5}
            onPageChange={(e) => {
              setFirst(e.first);
              setRows(e.rows);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
